import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Camera, Trash2, Users, Search, X } from "lucide-react";
import { PageHeader, StatusPill } from "../components/Primitives";
import { Modal, ConfirmDialog } from "../components/Modal";
import { api, errorText, inr, formatDate, formatTime12, todayISO } from "../lib/api";

const emptyForm = {
  name: "",
  mobile: "",
  email: "",
  father_name: "",
  mother_name: "",
  gender: "",
  dob: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  emergency_contact: "",
  aadhaar_number: "",
  joining_date: todayISO(),
  ending_date: "",
  monthly_fee: 1200,
  admission_fee: 0,
  discount: 0,
  initial_payment: 0,
  payment_mode: "CASH",
  shift_ids: [],
  seat_id: "",
  library_id: "",
  notes: "",
};

export default function Students({ user }) {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [seats, setSeats] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("ALL");
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [seatOptions, setSeatOptions] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const [studentsRes, shiftsRes, seatsRes] = await Promise.all([
        api.get("/students"),
        api.get("/shifts"),
        api.get("/seats"),
      ]);
      setStudents(studentsRes.data.data);
      setShifts(shiftsRes.data.data);
      setSeats(seatsRes.data.data);
      if (user.role === "MAIN_ADMIN") {
        const libRes = await api.get("/libraries");
        setLibraries(libRes.data.data);
      }
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, [user.role]);

  useEffect(() => {
    const params = form.shift_ids.length ? { params: { shift_ids: form.shift_ids.join(",") } } : {};
    api
      .get("/seats/availability", params)
      .then((res) => setSeatOptions(res.data.data))
      .catch(() => {});
  }, [form.shift_ids]);

  const freeSeatCount = useMemo(() => seatOptions.filter((seat) => seat.available).length, [seatOptions]);

  useEffect(() => () => { if (photoPreview) URL.revokeObjectURL(photoPreview); }, [photoPreview]);

  const filtered = useMemo(() => {
    return students.filter((student) => {
      if (status !== "ALL" && student.status !== status) return false;
      if (!query) return true;
      const needle = query.toLowerCase();
      return (
        student.name?.toLowerCase().includes(needle) ||
        student.mobile?.toLowerCase().includes(needle) ||
        student.email?.toLowerCase().includes(needle) ||
        student.student_id?.toLowerCase().includes(needle)
      );
    });
  }, [students, status, query]);

  const resetForm = () => {
    setForm(emptyForm);
    setPhotoFile(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
  };

  const toggleShift = (shiftId) => {
    setForm((previous) => {
      const current = new Set(previous.shift_ids);
      if (current.has(shiftId)) current.delete(shiftId);
      else current.add(shiftId);
      return { ...previous, shift_ids: Array.from(current) };
    });
  };

  const onPhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      setError("Photo must be under 3 MB");
      return;
    }
    setPhotoFile(file);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...form,
        monthly_fee: Number(form.monthly_fee || 0),
        admission_fee: Number(form.admission_fee || 0),
        discount: Number(form.discount || 0),
        initial_payment: Number(form.initial_payment || 0),
      };
      if (!payload.ending_date) delete payload.ending_date;
      if (!payload.email) delete payload.email;
      if (!payload.library_id) delete payload.library_id;
      if (!payload.seat_id) delete payload.seat_id;
      if (!payload.aadhaar_number) delete payload.aadhaar_number;
      if (!payload.dob) delete payload.dob;
      if (!payload.gender) delete payload.gender;
      const response = await api.post("/students", payload);
      const createdId = response.data?.data?.id;
      if (photoFile && createdId) {
        const formData = new FormData();
        formData.append("file", photoFile);
        try {
          await api.post(`/students/${createdId}/photo`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (uploadErr) {
          setError(`Student saved, but photo upload failed: ${errorText(uploadErr)}`);
        }
      }
      setShow(false);
      resetForm();
      load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShow(false);
    resetForm();
    setError("");
  };

  const removeStudent = async () => {
    try {
      await api.delete(`/students/${confirmDelete.id}`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      setError(errorText(err));
      setConfirmDelete(null);
    }
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="STUDENT DIRECTORY"
        title="Students"
        description="Admit new students, renew memberships and keep every payment linked."
        action={
          <button className="button button-primary" onClick={() => setShow(true)} data-testid="add-student-button">
            <Users size={17} /> Add student
          </button>
        }
      />
      {error && !show ? <div className="error-message" data-testid="students-error-message">{error}</div> : null}

      <section className="content-card table-card">
        <div className="table-toolbar">
          <div>
            <span className="card-kicker">ACTIVE RECORDS</span>
            <h2>{filtered.length} students</h2>
          </div>
          <div className="toolbar-actions">
            <div className="table-search">
              <Search size={17} />
              <input
                placeholder="Search by name, mobile or ID"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                data-testid="students-search-input"
              />
            </div>
            <select value={status} onChange={(event) => setStatus(event.target.value)} data-testid="students-status-filter">
              <option value="ALL">All</option>
              <option value="ACTIVE">Active</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>
        </div>
        <div className="table-wrap">
          <table data-testid="students-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Mobile</th>
                <th>Joining</th>
                <th>Ending</th>
                <th>Monthly fee</th>
                <th>Aadhaar</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((student) => (
                <tr key={student.id} data-testid={`student-row-${student.student_id}`} onClick={() => navigate(`/students/${student.id}`)} style={{ cursor: "pointer" }}>
                  <td>
                    <div className="person-cell">
                      <span className="table-avatar">{student.name?.charAt(0)}</span>
                      <div>
                        <strong>{student.name}</strong>
                        <small>{student.student_id}</small>
                      </div>
                    </div>
                  </td>
                  <td>{student.mobile}</td>
                  <td>{formatDate(student.joining_date)}</td>
                  <td>{formatDate(student.ending_date)}</td>
                  <td>{inr(student.monthly_fee)}</td>
                  <td>{student.aadhaar_masked || "—"}</td>
                  <td><StatusPill status={student.status} /></td>
                  <td>
                    <div className="action-row" onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        className="icon-button"
                        title="Remove student"
                        onClick={() => setConfirmDelete(student)}
                        data-testid={`delete-student-${student.student_id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {show ? (
        <Modal title="New admission" eyebrow="ADD STUDENT" close={closeModal} testId="add-student-modal" wide>
          <form className="student-form" onSubmit={submit}>
            {error ? <div className="error-message" data-testid="add-student-error">{error}</div> : null}

            <div className="form-section">
              <div className="form-section-head">
                <span className="hero-label">PHOTO</span>
                <h3>Profile picture</h3>
              </div>
              <div className="form-photo-row">
                <div className="form-photo-preview" data-testid="student-form-photo-preview">
                  {photoPreview ? <img src={photoPreview} alt="preview" /> : <span>{(form.name || "?").charAt(0).toUpperCase()}</span>}
                </div>
                <div className="form-photo-controls">
                  <label className="button button-outline" style={{ cursor: "pointer" }}>
                    <Camera size={16} /> {photoPreview ? "Replace photo" : "Upload photo"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={onPhotoChange} style={{ display: "none" }} data-testid="student-form-photo-input" />
                  </label>
                  {photoPreview ? (
                    <button type="button" className="button button-outline button-small" onClick={() => { setPhotoFile(null); if (photoPreview) URL.revokeObjectURL(photoPreview); setPhotoPreview(null); }} data-testid="student-form-photo-remove">
                      <X size={14} /> Remove
                    </button>
                  ) : null}
                  <small style={{ color: "#64748b" }}>JPG, PNG or WEBP · max 3 MB</small>
                </div>
              </div>
            </div>

            {user.role === "MAIN_ADMIN" ? (
              <div className="form-section">
                <div className="form-section-head">
                  <span className="hero-label">TENANT</span>
                  <h3>Which library?</h3>
                </div>
                <div className="form-grid">
                  <label className="field-full">Library
                    <select required value={form.library_id} onChange={(event) => setForm({ ...form, library_id: event.target.value })} data-testid="student-library-select">
                      <option value="">Select library</option>
                      {libraries.map((library) => <option key={library.id} value={library.id}>{library.name}</option>)}
                    </select>
                  </label>
                </div>
              </div>
            ) : null}

            <div className="form-section">
              <div className="form-section-head">
                <span className="hero-label">PERSONAL</span>
                <h3>Student details</h3>
              </div>
              <div className="form-grid">
                <label>Full name<input type="text" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="e.g. Riya Sharma" data-testid="student-name-input" /></label>
                <label>Mobile<input type="tel" required value={form.mobile} onChange={(event) => setForm({ ...form, mobile: event.target.value })} placeholder="+91 90000 00000" data-testid="student-mobile-input" /></label>
                <label>Email<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="student@example.com" /></label>
                <label>Gender
                  <select value={form.gender} onChange={(event) => setForm({ ...form, gender: event.target.value })}>
                    <option value="">Prefer not to say</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </label>
                <label>Date of birth<input type="date" value={form.dob} onChange={(event) => setForm({ ...form, dob: event.target.value })} /></label>
                <label>Father / guardian<input type="text" value={form.father_name} onChange={(event) => setForm({ ...form, father_name: event.target.value })} /></label>
                <label>Mother name<input type="text" value={form.mother_name} onChange={(event) => setForm({ ...form, mother_name: event.target.value })} /></label>
                <label>Emergency contact<input type="tel" value={form.emergency_contact} onChange={(event) => setForm({ ...form, emergency_contact: event.target.value })} /></label>
                <label className="field-full">Aadhaar (optional, kept masked)<input type="text" value={form.aadhaar_number} onChange={(event) => setForm({ ...form, aadhaar_number: event.target.value })} data-testid="student-aadhaar-input" placeholder="1234 5678 9012" /></label>
                <label className="field-full">Address<input type="text" value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} /></label>
                <label>City<input type="text" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} /></label>
                <label>State<input type="text" value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value })} /></label>
                <label>Pincode<input type="text" value={form.pincode} onChange={(event) => setForm({ ...form, pincode: event.target.value })} /></label>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-head">
                <span className="hero-label">MEMBERSHIP</span>
                <h3>Shifts, seat & dates</h3>
              </div>
              <div className="form-grid">
                <label>Joining date<input type="date" required value={form.joining_date} onChange={(event) => setForm({ ...form, joining_date: event.target.value })} data-testid="student-joining-date-input" /></label>
                <label>Ending date<input type="date" value={form.ending_date} onChange={(event) => setForm({ ...form, ending_date: event.target.value })} /></label>
                <label>Seat
                  <select value={form.seat_id} onChange={(event) => setForm({ ...form, seat_id: event.target.value })} data-testid="student-seat-select">
                    <option value="">None</option>
                    {seatOptions.filter((seat) => seat.available || seat.id === form.seat_id).map((seat) => (
                      <option key={seat.id} value={seat.id}>
                        {seat.number}{seat.occupied_shift_ids?.length ? ` (booked in ${seat.occupied_shift_ids.length} shift${seat.occupied_shift_ids.length > 1 ? "s" : ""})` : ""}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: "#16a34a" }} data-testid="seat-free-count">{freeSeatCount} seat{freeSeatCount === 1 ? "" : "s"} free for the selected shift(s)</small>
                </label>
              </div>
              <div className="field-label-row">
                <strong>Shifts (choose one or more)</strong>
                <small>{form.shift_ids.length} selected</small>
              </div>
              <div className="shift-chip-row" data-testid="student-shift-chips">
                {shifts.length === 0 ? (
                  <small style={{ color: "#64748b" }}>No shifts yet — create one from the Shifts page first.</small>
                ) : null}
                {shifts.map((shift) => {
                  const selected = form.shift_ids.includes(shift.id);
                  return (
                    <button
                      key={shift.id}
                      type="button"
                      className={`shift-chip ${selected ? "selected" : ""}`}
                      onClick={() => toggleShift(shift.id)}
                      data-testid={`shift-chip-${shift.id}`}
                      aria-pressed={selected}
                    >
                      <strong>{shift.name}</strong>
                      <small>{formatTime12(shift.start_time)} — {formatTime12(shift.end_time)}</small>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-head">
                <span className="hero-label">FEE</span>
                <h3>Billing & initial payment</h3>
              </div>
              <div className="form-grid">
                <label>Monthly fee<input type="number" min="0" value={form.monthly_fee} onChange={(event) => setForm({ ...form, monthly_fee: event.target.value })} data-testid="student-fee-input" /></label>
                <label>Admission fee<input type="number" min="0" value={form.admission_fee} onChange={(event) => setForm({ ...form, admission_fee: event.target.value })} /></label>
                <label>Discount<input type="number" min="0" value={form.discount} onChange={(event) => setForm({ ...form, discount: event.target.value })} /></label>
                <label>Initial payment<input type="number" min="0" value={form.initial_payment} onChange={(event) => setForm({ ...form, initial_payment: event.target.value })} data-testid="student-initial-payment-input" /></label>
                <label>Payment mode
                  <select value={form.payment_mode} onChange={(event) => setForm({ ...form, payment_mode: event.target.value })}>
                    <option value="CASH">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="BANK_TRANSFER">Bank transfer</option>
                    <option value="CARD">Card</option>
                    <option value="OTHER">Other</option>
                  </select>
                </label>
                <label className="field-full">Notes<input type="text" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Internal note (optional)" /></label>
              </div>
            </div>

            <div className="modal-actions sticky-actions">
              <button type="button" className="button button-outline" onClick={closeModal} data-testid="cancel-add-student-button">Cancel</button>
              <button className="button button-primary" type="submit" disabled={submitting} data-testid="save-student-button">
                {submitting ? "Saving…" : "Admit student"} <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title={`Remove ${confirmDelete.name}?`}
          message={`${confirmDelete.student_id} will be archived (soft-deleted) and their seat will be released. Payments and bills stay in the ledger for records.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={removeStudent}
          confirmLabel="Remove student"
          danger
          testId="remove-student-dialog"
        />
      ) : null}
    </div>
  );
}
